/**
 * AI CONTEXT
 * Purpose: "grade the graders" — per-teacher standout-moment writing stats
 *   for the Standout Review dashboard's Teacher Stats mode. A plain antd
 *   Table (not the Table2 building block — this is a computed summary, not
 *   a persisted admin table), sortable on every numeric column, default
 *   sort Total desc.
 * Used by: StandoutReviewView (Teacher stats mode).
 */
import { Table, Tooltip, Typography } from "antd"
import { type ColumnsType } from "antd/es/table"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { type HTMLAttributes, useCallback, useMemo } from "react"

import StaffNotesPopover from "#components/StaffNotes/StaffNotesPopover"
import { type ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { type StandoutMomentConstructor } from "#customTypes/composedTypes/standoutTypes"

import StandoutDistributionChart from "./StandoutDistributionChart"
import {
  computeTeacherStats,
  type StandoutReviewJoins,
  type StandoutTeacherStatsRow,
} from "./standoutReviewHelpers"

dayjs.extend(relativeTime)

const { Text } = Typography

interface StandoutTeacherStatsProps {
  moments: readonly StandoutMomentConstructor[]
  joins: Pick<StandoutReviewJoins, "studentsById" | "usersById">
  semesterClasses: readonly ClassConstructor[]
  /** UPDATE grant — gates the "Needs work ▼" (received reactions) column. */
  canSeeNeedsWorkReceived: boolean
}

const StandoutTeacherStats = ({
  moments,
  joins,
  semesterClasses,
  canSeeNeedsWorkReceived,
}: StandoutTeacherStatsProps) => {
  const rows = useMemo(
    () => computeTeacherStats(moments, joins, semesterClasses),
    [joins, moments, semesterClasses]
  )

  const columns: ColumnsType<StandoutTeacherStatsRow> = useMemo(
    () => [
      {
        title: "Teacher",
        dataIndex: "teacherName",
        key: "teacherName",
        sorter: (a, b) => a.teacherName.localeCompare(b.teacherName),
      },
      {
        // Grouped header: "Thumbs Up"/"Needs Work" here are the CATEGORIES
        // the teacher wrote moments in — without the band they read as
        // duplicates of the reviewer-reaction columns.
        title: "Moments written",
        children: [
          {
            title: "Total",
            dataIndex: "total",
            key: "total",
            sorter: (a, b) => a.total - b.total,
            defaultSortOrder: "descend",
          },
          {
            title: "Thumbs Up",
            dataIndex: "thumbsUp",
            key: "thumbsUp",
            sorter: (a, b) => a.thumbsUp - b.thumbsUp,
          },
          {
            title: "Needs Work",
            dataIndex: "needsWork",
            key: "needsWork",
            sorter: (a, b) => a.needsWork - b.needsWork,
          },
          {
            title: "Secret",
            dataIndex: "secret",
            key: "secret",
            sorter: (a, b) => a.secret - b.secret,
          },
          {
            title: "With Note %",
            dataIndex: "withNotePct",
            key: "withNotePct",
            sorter: (a, b) => a.withNotePct - b.withNotePct,
            render: (value: number) => `${value}%`,
          },
          {
            title: "Shared",
            dataIndex: "sharedCount",
            key: "sharedCount",
            sorter: (a, b) => a.sharedCount - b.sharedCount,
          },
          {
            title: "Students",
            dataIndex: "distinctStudents",
            key: "distinctStudents",
            sorter: (a, b) => a.distinctStudents - b.distinctStudents,
          },
          {
            title: "% Students w/ Feedback",
            dataIndex: "studentsWithFeedbackPct",
            key: "studentsWithFeedbackPct",
            sorter: (a, b) =>
              (a.studentsWithFeedbackPct ?? -1) -
              (b.studentsWithFeedbackPct ?? -1),
            render: (value: number | null, row) =>
              value === null ? (
                <Text type="secondary">—</Text>
              ) : (
                <Tooltip
                  title={`${row.rosterSize} student${row.rosterSize === 1 ? "" : "s"} on this teacher's semester rosters`}
                >
                  {value}%
                </Tooltip>
              ),
          },
        ],
      },
      {
        title: "Review reactions received",
        children: [
          {
            title: "Likes ▲",
            dataIndex: "likesReceived",
            key: "likesReceived",
            sorter: (a, b) => a.likesReceived - b.likesReceived,
          },
          // Thumbs-down marks are visible only to the moderation grant plus
          // the moment's own author (owner ask) — the received-reactions
          // column reflects that same gate at the aggregate level.
          ...(canSeeNeedsWorkReceived
            ? [
                {
                  title: "Needs work ▼",
                  dataIndex: "needsWorkReceived",
                  key: "needsWorkReceived",
                  sorter: (
                    a: StandoutTeacherStatsRow,
                    b: StandoutTeacherStatsRow
                  ) => a.needsWorkReceived - b.needsWorkReceived,
                },
              ]
            : []),
        ],
      },
      {
        title: "Last Activity",
        dataIndex: "lastActivity",
        key: "lastActivity",
        sorter: (a, b) =>
          dayjs(a.lastActivity).valueOf() - dayjs(b.lastActivity).valueOf(),
        render: (value: string) =>
          value ? (
            <Text type="secondary">{dayjs(value).fromNow()}</Text>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: "",
        key: "staffNote",
        render: (_, row) => (
          <StaffNotesPopover
            subjectUserId={row.teacherId}
            subjectName={row.teacherName}
            buttonSize="small"
          />
        ),
      },
    ],
    [canSeeNeedsWorkReceived]
  )

  const expandedRowRender = useCallback(
    (row: StandoutTeacherStatsRow) => (
      <StandoutDistributionChart
        teacherId={row.teacherId}
        moments={moments}
        semesterClasses={semesterClasses}
        studentsById={joins.studentsById}
      />
    ),
    [joins.studentsById, moments, semesterClasses]
  )

  return (
    // Wrapped in a div: a data-testid on the antd Table itself is not
    // reliably forwarded to the DOM (see test-environment skill).
    <div data-testid="sr-stats-table">
      <Table<StandoutTeacherStatsRow>
        rowKey="teacherId"
        columns={columns}
        dataSource={rows}
        pagination={false}
        expandable={{ expandedRowRender }}
        onRow={(row) =>
          ({ "data-teacher-id": row.teacherId }) as HTMLAttributes<HTMLElement>
        }
      />
    </div>
  )
}

export default StandoutTeacherStats
